import React, { useEffect, useMemo, useRef, useState } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import { T, STATUS, MEJA, POI, KARAKTER, WARNA_AGEN, DURASI, TINGGI_DUDUK, DIALOG } from "./office";

// Karakter NPC blocky gaya Studio AI: kepala + rambut/topi/kacamata, badan, lengan & kaki berengsel (paha-betis).
// Perilaku: status working/handoff/needs_approval → duduk di meja & mengetik. Idle → lama di meja, lalu sesekali pindah ke satu
// aktivitas panjang (rebahan tidur di sofa, duduk santai, berdiri di pantry/pingpong), jarang mondar-mandir. Dua agen idle yang
// berdekatan sesekali berbincang (gelembung tanya → jawab). Klik karakter/badge → onPilih(key).
const KECEPATAN = 1.6;
const terisi = new Set();   // POI yang sedang ditempati
export const papan = {};           // posisi & keadaan tiap agen (untuk deteksi bertemu)
const obrolan = {};         // key → { teks, mulai, sampai }
export const bicara = (key, teks, durasi = 6) => { obrolan[key] = { teks, mulai: null, durasi }; }; // gelembung dari luar (mis. transkrip rapat)
const acak = (a, b) => a + Math.random() * (b - a);
const durasi = j => acak(...DURASI[j]);
const kursiMeja = k => { const [x, z, r] = MEJA[k]; return { x: x + 0.3 * Math.sin(r), z: z + 0.3 * Math.cos(r), rot: r, aksi: "meja" }; };
const belakangKursi = k => { const [x, z, r] = MEJA[k]; return [x + 1.2 * Math.sin(r), z + 1.2 * Math.cos(r)]; };
const sudut = (dari, ke) => { let d = ke - dari; while (d > Math.PI) d -= Math.PI * 2; while (d < -Math.PI) d += Math.PI * 2; return d; };
const pilihPoi = () => {
  const bebas = POI.map((_, i) => i).filter(i => !terisi.has(i));
  if (!bebas.length) return -1;
  const r = Math.random(), jenis = r < .25 ? "tidur" : r < .55 ? "sofa" : r < .7 ? "kursi" : "berdiri";
  const cocok = bebas.filter(i => POI[i][3] === jenis);
  return (cocok.length ? cocok : bebas)[Math.floor(Math.random() * (cocok.length ? cocok : bebas).length)];
};

const Bx = ({ p, s, c, o }) => <mesh position={p} castShadow><boxGeometry args={s} /><meshStandardMaterial color={c} transparent={o != null} opacity={o ?? 1} roughness={.9} /></mesh>;
const GELAP = "#2b2f3a";
const Kaki = ({ x, r, K }) => (
  <group ref={r.paha} position={[x, 0.5, 0]}>
    <Bx p={[0, -0.13, 0]} s={[0.18, 0.26, 0.2]} c={K.celana} />
    <group ref={r.betis} position={[0, -0.26, 0]}><Bx p={[0, -0.12, 0]} s={[0.17, 0.24, 0.19]} c={K.celana} /><Bx p={[0, -0.22, -0.03]} s={[0.18, 0.08, 0.26]} c={GELAP} /></group>
  </group>
);
const Lengan = ({ x, r, K }) => (
  <group ref={r} position={[x, 1.0, 0]}>
    <Bx p={[0, -0.13, 0]} s={[0.14, 0.26, 0.16]} c={K.baju} /><Bx p={[0, -0.37, 0]} s={[0.13, 0.24, 0.15]} c={K.kulit} />
  </group>
);

function Model({ k, refs }) {
  const K = KARAKTER[k] || KARAKTER.memory;
  return (
    <>
      <Kaki x={-0.11} r={refs.kiri} K={K} /><Kaki x={0.11} r={refs.kanan} K={K} />
      <group ref={refs.badan}>
        <Bx p={[0, 0.78, 0]} s={[0.46, 0.52, 0.28]} c={K.baju} /><Bx p={[0, 0.53, 0]} s={[0.47, 0.06, 0.29]} c={GELAP} />
        {K.dasi && <Bx p={[0, 0.84, -0.145]} s={[0.08, 0.3, 0.02]} c={K.dasi} />}
        <Lengan x={-0.3} r={refs.lkiri} K={K} /><Lengan x={0.3} r={refs.lkanan} K={K} />
        <group ref={refs.kepala} position={[0, 1.06, 0]}>
          <Bx p={[0, 0.18, 0]} s={[0.36, 0.36, 0.36]} c={K.kulit} />
          <group ref={refs.mata}>{[-0.08, 0.08].map(x => <Bx key={x} p={[x, 0.2, -0.185]} s={[0.06, 0.07, 0.02]} c={GELAP} />)}</group>
          <Bx p={[0, 0.08, -0.185]} s={[0.1, 0.025, 0.02]} c="#b86a5a" />
          {K.kacamata && [-0.08, 0.08].map(x => <Bx key={x} p={[x, 0.2, -0.195]} s={[0.12, 0.1, 0.02]} c="#1a1a1a" o={.55} />)}
          <Bx p={[0, 0.33, 0]} s={[0.38, 0.12, 0.38]} c={K.rambut} /><Bx p={[0, 0.2, 0.15]} s={[0.38, 0.2, 0.1]} c={K.rambut} />
          {K.rambutPanjang && <Bx p={[0, 0.0, 0.18]} s={[0.38, 0.3, 0.1]} c={K.rambut} />}
          {K.topi && <><Bx p={[0, 0.38, 0]} s={[0.4, 0.1, 0.4]} c={K.topi} /><Bx p={[0, 0.35, -0.28]} s={[0.4, 0.04, 0.2]} c={K.topi} /></>}
        </group>
      </group>
    </>
  );
}

const Gelembung = ({ teks, tidur }) => (
  <div data-testid="gelembung-obrolan" style={{ position: "relative", fontFamily: T.font, fontSize: tidur ? 13 : 11, fontWeight: 700, color: tidur ? T.accent : T.ink, background: "rgba(255,255,255,.96)", border: `1px solid ${T.border}`, borderRadius: 14, padding: "6px 10px", whiteSpace: "nowrap", boxShadow: "0 8px 20px rgba(20,20,20,.12)", animation: "jw-float 2.2s ease-in-out infinite", maxWidth: 220 }}>
    {teks}
    <span style={{ position: "absolute", left: "50%", bottom: -6, width: 10, height: 10, background: "rgba(255,255,255,.96)", borderRight: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, transform: "translateX(-50%) rotate(45deg)" }} />
  </div>
);

export function Karakter({ a, dipilih, onPilih, kursi }) {
  const root = useRef(), tubuh = useRef();
  const refs = useMemo(() => ({ kiri: { paha: React.createRef(), betis: React.createRef() }, kanan: { paha: React.createRef(), betis: React.createRef() }, badan: React.createRef(), lkiri: React.createRef(), lkanan: React.createRef(), kepala: React.createRef(), mata: React.createRef() }), []);
  const st = a.status || "idle", warna = WARNA_AGEN[a.zona];
  const [gelembung, setGelembung] = useState(null);
  const S = useRef(null);
  if (!S.current) { const m = kursiMeja(a.key); S.current = { mode: "sit", x: m.x, z: m.z, rot: m.rot, tujuan: m, jalur: [], timer: acak(10, 70), poi: -1, fase: Math.random() * 10, cek: Math.random() * 2, diamSampai: 0, gelembung: null }; }
  useEffect(() => () => { if (S.current.poi >= 0) terisi.delete(S.current.poi); delete papan[a.key]; delete obrolan[a.key]; }, [a.key]);

  useFrame(({ clock }, dt) => {
    const s = S.current, g = root.current, tb = tubuh.current; if (!g || !tb) return;
    const dtNyata = Math.min(dt, 1); dt = Math.min(dt, 0.1); // timer pakai waktu nyata, gerak dibatasi agar tidak lompat saat fps rendah
    const now = clock.getElapsedTime(), t = now + s.fase, sibuk = st !== "idle";
    // ---- keputusan ----
    const diMeja = s.tujuan.aksi === "meja", lepasPoi = () => { if (s.poi >= 0) { terisi.delete(s.poi); s.poi = -1; } };
    const pergi = t => { lepasPoi(); s.jalur = s.tujuan.aksi === "meja" ? [belakangKursi(a.key), [t.x, t.z]] : [[t.x, t.z]]; s.tujuan = t; s.mode = "walk"; };
    const pulang = () => pergi(kursiMeja(a.key));
    const rapat = st === "meeting" && kursi;
    if (rapat) { if (!s.tujuan.rapat) pergi(kursi); }
    else if (sibuk) { if (!diMeja) pulang(); }
    else if (s.tujuan.rapat && s.mode !== "walk") pulang();
    else if (s.mode !== "walk") {
      s.timer -= dtNyata;
      if (s.timer <= 0) {
        const i = (diMeja || Math.random() < 0.3) ? pilihPoi() : -1;
        if (i >= 0) {
          const [x, z, rot, aksi] = POI[i];
          lepasPoi(); terisi.add(i); s.poi = i;
          s.jalur = diMeja ? [belakangKursi(a.key), [x, z]] : [[x, z]];
          s.tujuan = { x, z, rot, aksi }; s.mode = "walk";
        } else if (diMeja) s.timer = acak(20, 60); else pulang();
      }
    }
    // ---- gerak ----
    let langkah = 0;
    if (s.mode === "walk") {
      const [tx, tz] = s.jalur[0], dx = tx - s.x, dz = tz - s.z, jarak = Math.hypot(dx, dz);
      if (jarak < 0.05) { s.jalur.shift(); if (!s.jalur.length) { s.mode = s.tujuan.aksi === "berdiri" ? "stand" : s.tujuan.aksi === "tidur" ? "tidur" : "sit"; s.rot = s.tujuan.rot; s.timer = durasi(s.tujuan.aksi); } }
      else { const m = Math.min(jarak, KECEPATAN * dt); s.x += dx / jarak * m; s.z += dz / jarak * m; s.rot += sudut(s.rot, Math.atan2(-dx, -dz)) * Math.min(1, dt * 10); langkah = 1; }
    } else s.rot += sudut(s.rot, s.tujuan.rot) * Math.min(1, dt * 8);
    papan[a.key] = { x: s.x, z: s.z, santai: !sibuk && s.mode !== "walk" && s.mode !== "tidur" && s.tujuan.aksi !== "meja" && !s.tujuan.rapat, diamSampai: s.diamSampai };
    // ---- obrolan: dua agen santai berdekatan → tanya-jawab, lalu jeda panjang ----
    s.cek -= dtNyata;
    if (s.cek <= 0) {
      s.cek = 1.5;
      const saya = papan[a.key];
      if (saya.santai && now > s.diamSampai && !obrolan[a.key]) {
        for (const [k, o] of Object.entries(papan)) {
          if (k <= a.key || !o.santai || now < o.diamSampai || obrolan[k] || Math.hypot(o.x - s.x, o.z - s.z) > 2.8) continue;
          if (Math.random() < 0.5) break;
          const [tanya, jawab] = DIALOG[Math.floor(Math.random() * DIALOG.length)];
          obrolan[a.key] = { teks: tanya, mulai: now, sampai: now + 4 }; obrolan[k] = { teks: jawab, mulai: now + 4.3, sampai: now + 8.3 };
          s.diamSampai = now + acak(25, 60); if (papan[k]) papan[k].diamSampai = s.diamSampai;
          break;
        }
      }
    }
    const ob = obrolan[a.key];
    if (ob && ob.mulai == null) { ob.mulai = now; ob.sampai = now + ob.durasi; }
    if (ob && now > ob.sampai) { delete obrolan[a.key]; if (now > s.diamSampai) s.diamSampai = now + acak(25, 60); }
    const teksGelembung = s.mode === "tidur" ? "Zzz…" : ob && now >= ob.mulai ? ob.teks : null;
    if (teksGelembung !== s.gelembung) { s.gelembung = teksGelembung; setGelembung(teksGelembung); }
    // ---- pose ----
    const duduk = s.mode === "sit", tidur = s.mode === "tidur", R = refs, w = Math.sin(t * 9), aksi = s.tujuan.aksi;
    g.position.set(s.x, 0, s.z);
    g.rotation.y = s.rot + (tidur ? Math.PI : 0);
    tb.rotation.x = tidur ? Math.PI / 2 : 0;
    tb.position.y = tidur ? TINGGI_DUDUK.tidur + 0.15 : duduk ? TINGGI_DUDUK[aksi] - 0.5 : langkah ? Math.abs(Math.cos(t * 9)) * 0.03 : 0;
    const santaiSofa = duduk && aksi === "sofa";
    const paha = duduk ? (santaiSofa ? Math.PI / 2.4 : Math.PI / 2) : langkah ? 0.6 * w : 0, betis = duduk ? (santaiSofa ? -Math.PI / 3 : -Math.PI / 2) : langkah ? Math.max(0, -0.8 * w) : 0;
    R.kiri.paha.current.rotation.x = paha; R.kanan.paha.current.rotation.x = duduk ? paha : -paha;
    R.kiri.betis.current.rotation.x = duduk ? betis : Math.max(0, -0.9 * w); R.kanan.betis.current.rotation.x = duduk ? betis : Math.max(0, 0.9 * w);
    const bekerja = duduk && sibuk && !rapat, diskusi = rapat && s.mode !== "walk"; // di meja rapat: gestur tangan sambil bicara
    const lk = bekerja ? 1.15 + 0.08 * Math.sin(t * 16) : diskusi ? 0.5 + 0.3 * Math.sin(t * 2.1) : tidur ? 0.25 : duduk ? (santaiSofa ? 0.2 : 0.55) : langkah ? -0.6 * w : 0.08 * Math.sin(t * 1.3);
    const ln = bekerja ? 1.15 + 0.08 * Math.sin(t * 16 + 1.5) : diskusi ? 0.5 + 0.3 * Math.sin(t * 1.7 + 1) : tidur ? 0.25 : duduk ? (santaiSofa ? 0.2 : 0.55) : langkah ? 0.6 * w : -0.08 * Math.sin(t * 1.3);
    R.lkiri.current.rotation.x = lk; R.lkanan.current.rotation.x = st === "handoff" ? -2.6 + 0.3 * Math.sin(t * 6) : ln;
    R.lkanan.current.rotation.z = st === "handoff" ? -0.4 : 0;
    R.kepala.current.rotation.y = st === "needs_approval" ? 0.5 * Math.sin(t * 2.5) : langkah || tidur ? 0 : 0.25 * Math.sin(t * 0.7);
    R.kepala.current.rotation.x = bekerja ? 0.15 : tidur ? 0.1 : diskusi ? 0.08 * Math.sin(t * 2.6) : 0;
    R.mata.current.scale.y = tidur ? 0.15 : (Math.sin(t * 1.1) > 0.97 ? 0.15 : 1); // kedip / terpejam
    R.badan.current.position.y = 0.012 * Math.sin(t * (tidur ? 1.2 : 2.2));
  });

  const tidur = gelembung === "Zzz…";
  return (
    <group ref={root} onClick={e => { e.stopPropagation(); onPilih(a.key); }} onPointerOver={() => (document.body.style.cursor = "pointer")} onPointerOut={() => (document.body.style.cursor = "")}>
      <group ref={tubuh}><Model k={a.key} refs={refs} /></group>
      <Html position={[0, tidur ? 1.15 : 1.95, 0]} center zIndexRange={[5, 0]}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          {gelembung && <Gelembung teks={gelembung} tidur={tidur} />}
          <div data-testid={`badge-${a.key}`} onClick={e => { e.stopPropagation(); onPilih(a.key); }} style={{ cursor: "pointer", fontFamily: T.font, background: "rgba(255,255,255,.93)", border: `1px solid ${dipilih ? T.accent : T.border}`, borderRadius: 12, padding: "4px 9px", color: T.ink, whiteSpace: "nowrap", boxShadow: "0 8px 24px rgba(20,20,20,.1)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 800 }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: T[st], boxShadow: `0 0 8px ${T[st]}`, animation: st === "needs_approval" ? "jw-blink .6s steps(2) infinite" : st === "working" ? "jw-blink 1.2s ease-in-out infinite" : "none" }} />
              {a.nama}
            </div>
            {(dipilih || st !== "idle") && <div style={{ fontSize: 10.5, fontWeight: 700, color: T[st], marginTop: 2 }}>{st === "needs_approval" ? "⚠ " : ""}{STATUS[st]}{a.tugas ? <span style={{ color: T.sub, fontWeight: 600 }}> · {a.tugas.slice(0, 28)}</span> : null}</div>}
            {dipilih && <div style={{ fontSize: 10, color: T.sub }}>{a.role}</div>}
          </div>
        </div>
      </Html>
      <mesh position={[0, 0.005, 0]} rotation={[-Math.PI / 2, 0, 0]}><circleGeometry args={[0.3, 16]} /><meshBasicMaterial color="#000" transparent opacity={.12} /></mesh>
      {dipilih && <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}><ringGeometry args={[0.5, 0.6, 32]} /><meshBasicMaterial color={warna} /></mesh>}
    </group>
  );
}
